import React, { useMemo, useState } from 'react';
import { useGetContacts } from '../../hooks/contacts/useGetContacts';
import { useDeleteContact } from '../../hooks/contacts/useDeleteContact';
import { useDeleteAllContacts } from '../../hooks/contacts/useDeleteAllContacts';
import { useRestoreContact } from '../../hooks/contacts/useRestoreContact';
import { useForceDeleteContact } from '../../hooks/contacts/useForceDeleteContact';
import { useRestoreAllContacts } from '../../hooks/contacts/useRestoreAllContacts';
import { useForceDeleteAllContacts } from '../../hooks/contacts/useForceDeleteAllContacts';
import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ContactsTableRow from './Table/ContactsTableRow';
import { ContactsTableColumns } from './Table/ContactsTableColumns';
import { usePermissions } from '../../auth/PermissionsContext';

const ContactsTable = () => {
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has('viewAny-contact'),
            view: has('view-contact'),
            delete: has('delete-contact'),
            restore: has('restore-contact'),
        }),
        [has]
    );

    const [trashedFilter, setTrashedFilter] = useState('');

    const { deleteContactMutate, isPending: isDelete } = useDeleteContact();
    const { restoreContactMutate } = useRestoreContact();
    const { forceDeleteContactMutate, isPending: isForceDelete } = useForceDeleteContact();
    const { deleteAllContactsMutate, isPending: isMassDeleting } = useDeleteAllContacts();
    const { restoreAllContactsMutate, isPending: isMassRestoring } = useRestoreAllContacts();
    const { forceDeleteAllContactsMutate, isPending: isMassForceDeleting } = useForceDeleteAllContacts();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleView,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/contacts',
        deleteSingle: deleteContactMutate,
        restoreSingle: restoreContactMutate,
        forceDeleteSingle: forceDeleteContactMutate,
        deleteAll: deleteAllContactsMutate,
        restoreAll: restoreAllContactsMutate,
        forceDeleteAll: forceDeleteAllContactsMutate,
        isArchive: trashedFilter === 'only',
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === 'only' ? 'only' : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, order]
    );

    const { isLoading, error, contacts, meta } = useGetContacts(params);

    const filters = [];

    const columns = useMemo(() => ContactsTableColumns, []);

    return (
        <TableLayout
            title={{ ar: 'رسائل التواصل', en: 'Contact Messages' }}
            columns={columns}
            items={contacts}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, contacts)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isMassDeleting || isMassForceDeleting}
            isBulkRestoring={isMassRestoring}
            isArchive={trashedFilter === "only"}
            onAddClick={undefined}
            addButtonVisible={false}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuView={perms.view ? handleView : undefined}
            onMenuRestore={handleMenuRestore}
            onMenuDelete={handleMenuDelete}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDelete}
            renderRow={(params) => (
                <ContactsTableRow
                    {...params}
                    key={params.item.id}
                    contact={params.item}
                    perms={perms}
                    handleMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default ContactsTable;
